import React, { useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';
import { Modal } from './Modal';
import { longDate, todayIso } from './dates';
import { IconUpload } from './Icons';
import { PaperInfo } from './types';

interface Props {
  initialDate: string;
  isAdmin: boolean;
  onClose: () => void;
  onDone: (p: PaperInfo) => void;
  onDeleted?: (date: string) => void;
}

export function UploadDialog({ initialDate, isAdmin, onClose, onDone, onDeleted }: Props) {
  const [date, setDate] = useState(initialDate);
  const [file, setFile] = useState<File | null>(null);
  const [exists, setExists] = useState<PaperInfo | null>(null);
  const [checking, setChecking] = useState(false);
  const [pct, setPct] = useState<number | null>(null);
  const [err, setErr] = useState('');
  const [drag, setDrag] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    setChecking(true);
    setExists(null);
    api
      .paper(date)
      .then((p) => live && setExists(p))
      .catch(() => live && setExists(null))
      .finally(() => live && setChecking(false));
    return () => {
      live = false;
    };
  }, [date]);

  const pick = (f: File | undefined | null) => {
    setErr('');
    if (!f) return;
    if (!/\.pdf$/i.test(f.name) && f.type !== 'application/pdf') {
      setErr('Please choose a PDF file.');
      return;
    }
    setFile(f);
  };

  const mine = !!exists; // everyone signed in can delete a day and upload it again
  const blocked = false;
  const replace = !!exists && isAdmin;

  const remove = async () => {
    if (!exists) return;
    if (!window.confirm(`Delete the newspaper of ${longDate(date)}? After that you can upload a new PDF for the same day.`)) return;
    setErr('');
    try {
      await api.deleteMyPaper(date);
      setExists(null);
      setFile(null);
      onDeleted?.(date);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not delete the newspaper.');
    }
  };

  const submit = async () => {
    if (!file) return;
    setErr('');
    setPct(0);
    try {
      const p = await api.upload(date, file, replace, setPct);
      onDone(p);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Upload failed.');
      setPct(null);
    }
  };

  return (
    <Modal title={replace ? 'Replace newspaper (admin)' : 'Upload newspaper'} onClose={pct === null ? onClose : () => undefined}>
      <label htmlFor="up-date">Newspaper date</label>
      <input id="up-date" type="date" value={date} max={todayIso()} onChange={(e) => e.target.value && setDate(e.target.value)} disabled={pct !== null} />
      <p className="muted small">{longDate(date)}</p>

      {checking && <p className="muted small">Checking this date…</p>}
      {blocked && (
        <p className="error">
          The newspaper for this date is already uploaded ({exists!.pageCount} pages, by {exists!.uploadedBy}). Only the person who uploaded it, or the admin, can delete it – please ask them.
        </p>
      )}
      {mine && !replace && (
        <p className="warn">
          A newspaper is already uploaded for this date ({exists!.pageCount} pages, by {exists!.uploadedBy}). To upload a new one, delete this one first.
        </p>
      )}
      {mine && (
        <button className="btn danger block" onClick={remove} disabled={pct !== null}>
          Delete this newspaper
        </button>
      )}
      {replace && <p className="warn">A newspaper is already stored for this date. Uploading will replace it for everyone.</p>}

      {!blocked && !(mine && !replace) && (
        <div
          className={'drop' + (drag ? ' over' : '')}
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            pick(e.dataTransfer.files?.[0]);
          }}
          onClick={() => input.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && input.current?.click()}
        >
          <IconUpload size={28} />
          <strong>{file ? file.name : 'Choose the PDF or drop it here'}</strong>
          <span className="muted small">{file ? (file.size / 1048576).toFixed(1) + ' MB' : 'One PDF with all pages of the day (up to 250 MB)'}</span>
          <input ref={input} type="file" accept="application/pdf,.pdf" hidden onChange={(e) => pick(e.target.files?.[0])} />
        </div>
      )}

      {pct !== null && (
        <div className="progress" aria-label="Upload progress">
          <div style={{ width: pct + '%' }} />
          <span>{pct < 100 ? pct + '%' : 'Preparing pages…'}</span>
        </div>
      )}
      {err && <p className="error" role="alert">{err}</p>}

      <div className="actions">
        <button className="btn" onClick={onClose} disabled={pct !== null}>
          Cancel
        </button>
        <button className="btn primary" onClick={submit} disabled={!file || blocked || (mine && !replace) || pct !== null || checking}>
          {replace ? 'Replace' : 'Upload'}
        </button>
      </div>
    </Modal>
  );
}
